import React from 'react';
import { Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, Panel, RevealText, SearchField, SegmentedControl, StatusChip, type ListColumn,
} from '../../../ui-kit';
import { discardLead, fetchQueue, watchQueue, type QueueLead } from '../../../services/roccoService';
import QueueRowMenu from '../QueueRowMenu';
import { fromNow, matches, useLive, when } from './live';
import ListDownload from './ListDownload';
import AgentName from './AgentName';
import LeadStoryModal from './LeadStory';

/**
 * New leads, as they come in, until the AI books them a time.
 *
 * A lead appears here seconds after it lands in HubSpot, is given an AI agent,
 * and is called. A call in progress sits at the top. The moment a call books a
 * time the lead leaves, for Scheduled callbacks and a person — so on a quiet
 * day this list is short or empty, which is the point. Updates by itself.
 */
export default function QueuePage() {
  // The one section the server pushes to: a new lead or a call moving shows
  // the moment it happens, not on the next tick.
  const { data, error, loading, streaming, reconnecting, reload } = useLive(fetchQueue, 5000, { watch: watchQueue });
  const [openLead, setOpenLead] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState<'all' | 'calling' | 'waiting' | 'too_old'>('all');
  const all = data?.leads ?? [];
  const onCall = all.filter((lead) => lead.status === 'calling');
  // Leads that reached ROCCO over an hour late: shown for a day, never called.
  const tooOld = all.filter((lead) => lead.status === 'too_old');
  const waitingLeads = all.filter((lead) => lead.status !== 'calling' && lead.status !== 'too_old');
  const leads = (show === 'calling' ? onCall : show === 'waiting' ? waitingLeads : show === 'too_old' && tooOld.length ? tooOld : all)
    .filter((lead) => matches(query, [lead.name, lead.phone, lead.owner, lead.channel, lead.state, lead.last_outcome]));

  const columns: ListColumn<QueueLead>[] = [
    {
      key: 'lead',
      sortValue: (lead) => lead.name,
      header: 'Lead',
      grow: 1.2,
      minWidth: 140,
      render: (lead) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{lead.name}</Typography>
          <RevealText value={lead.phone} what="phone number" />
        </Stack>
      ),
    },
    {
      key: 'arrived',
      sortValue: (lead) => lead.arrived_at, sortStartsDescending: true,
      header: 'Arrived',
      width: 120,
      render: (lead) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {when(lead.arrived_at)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {fromNow(lead.arrived_at)}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'from',
      sortValue: (lead) => lead.channel,
      header: 'From',
      grow: 1,
      minWidth: 120,
      render: (lead) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{lead.source}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[lead.channel, lead.state].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'owner',
      sortValue: (lead) => lead.owner,
      header: 'AI agent',
      width: 110,
      render: (lead) => lead.owner
        ? <AgentName person={{ name: lead.owner, kind: lead.owner_kind || 'ai' }} />
        : (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {lead.status === 'too_old' ? 'None' : 'Assigning…'}
          </Typography>
        ),
    },
    {
      key: 'calls',
      sortValue: (lead) => lead.attempts, sortStartsDescending: true,
      header: 'Calls',
      width: 56,
      align: 'right',
      render: (lead) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
          {lead.attempts}
        </Typography>
      ),
    },
    {
      key: 'status',
      sortValue: (lead) => (lead.status === 'calling' ? 0 : lead.status === 'too_old' ? 3 : lead.next_call_at ? 2 : 1),
      header: 'Now',
      // Wide enough for the longest chip: "Tries again Thu 1 Oct, 12:38 pm".
      width: 240,
      render: (lead) => <NowChip lead={lead} />,
    },
    {
      key: 'last',
      sortValue: (lead) => lead.last_outcome,
      header: 'Last call',
      grow: 1.2,
      minWidth: 140,
      render: (lead) => (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {lead.last_outcome || (lead.status === 'too_old'
            ? 'Not called: it reached ROCCO more than an hour after it arrived'
            : 'Not called yet')}
        </Typography>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 72,
      align: 'right',
      render: (lead) => (
        <QueueRowMenu
          what="lead"
          onCall={lead.status === 'calling'}
          confirmTitle={`Discard ${lead.name}?`}
          confirmText={`${lead.name} leaves the queue and is never called again. `
            + 'Their story keeps a note that you discarded them.'}
          onDiscard={async () => { await discardLead(lead.id); reload(); }}
        />
      ),
    },
  ];

  const waiting = waitingLeads.length + onCall.length;
  const calling = data?.calling_now ?? 0;
  const table = (
    <ListTable
      columns={columns}
      rows={leads}
      getRowKey={(lead) => lead.id}
      pageSize={25}
      loading={loading}
      onRowClick={(lead) => setOpenLead(lead.id)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No new leads waiting. Create a contact in HubSpot and it appears here within seconds, then the AI calls it.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search leads…" />;
  const picker = (
    <SegmentedControl
      value={show}
      onChange={setShow}
      ariaLabel="Which leads"
      options={[
        { value: 'all', label: `All · ${all.length}` },
        { value: 'calling', label: `On a call · ${onCall.length}` },
        { value: 'waiting', label: `Waiting · ${waitingLeads.length}` },
        ...(tooOld.length ? [{ value: 'too_old' as const, label: `Too old · ${tooOld.length}` }] : []),
      ]}
    />
  );
  const download = <ListDownload list="queue" total={all.length} noun="leads in the queue" />;

  return (
    <>
      <Panel
        title="Live queue"
        subtitle={`${waiting} lead${waiting === 1 ? '' : 's'} waiting · ${calling} on a call now${tooOld.length ? ` · ${tooOld.length} too old to call` : ''} · ${reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself'}`}
        search={searchBox}
        tools={picker}
        actions={download}
        expanded={{ content: table, actions: <>{searchBox}{picker}{download}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>
      {openLead ? <LeadStoryModal leadId={openLead} onClose={() => setOpenLead(null)} /> : null}
    </>
  );
}

function NowChip({ lead }: { lead: QueueLead }) {
  if (lead.status === 'calling') return <StatusChip tone="good" label="On a call" />;
  if (lead.status === 'too_old') return <StatusChip tone="warning" label="Too old to call" />;
  if (lead.status === 'new' || !lead.owner) return <StatusChip tone="neutral" label="Just arrived" />;
  if (lead.next_call_at && new Date(lead.next_call_at).getTime() > Date.now()) {
    return <StatusChip tone="neutral" label={`Tries again ${when(lead.next_call_at)}`} />;
  }
  return <StatusChip tone="neutral" label="Waiting" />;
}
